// WHY (#2116): CONTAINER request-level weight-and-volume block. Mirrors the
//   #1448 STANDARD totals card — the redesigned CONTAINER form captures the
//   request-level total weight (kg) + volume (m³) directly (no per-item
//   Positions editor) and the backend send-gate now requires both > 0. Both
//   inputs use the #1437 decimal-safe text buffer so an in-progress fractional
//   value (`0.75`, `12.5`) persists without collapsing.
//
// @aiPurpose Edit the CONTAINER request-level `totalWeight` / `totalVolume`
//   that map to the #1447 wire `weight` / `volume` draft-save fields.

import { useTranslation } from 'react-i18next';
import { Eyebrow, Input } from '../../../ui/index.js';
import { useRequestsStore } from '../store.js';
import { Card, Field, Grid2 } from './DraftEditorPrimitives.js';
import { useDecimalTextInput } from './useDecimalTextInput.js';
import type { DraftScratchpad } from '../types.js';

export function DraftContainerTotalsCard({ d }: { d: DraftScratchpad }) {
  const { t } = useTranslation(['requests']);
  const patchHeader = useRequestsStore((s) => s.patchDraftHeader);
  // WHY (#1437): bind the local decimal buffer to the store value via the
  //   shared hook — NEVER bind the Input directly to a parsed number.
  const weightField = useDecimalTextInput(d.totalWeight, (totalWeight) =>
    patchHeader({ totalWeight }),
  );
  const volumeField = useDecimalTextInput(d.totalVolume, (totalVolume) =>
    patchHeader({ totalVolume }),
  );
  return (
    <Card>
      <div data-testid="draft-container-totals-card">
        <Eyebrow>{t('requests:draft.containerTotals.section')}</Eyebrow>
        <div style={{ height: 12 }} />
        <Grid2>
          <Field label={t('requests:draft.containerTotals.weight')} required>
            <Input
              monospace
              value={weightField.text}
              onChange={weightField.onChange}
              onBlur={weightField.onBlur}
              placeholder="0"
              ariaLabel={t('requests:draft.containerTotals.weight')}
            />
          </Field>
          <Field label={t('requests:draft.containerTotals.volume')} required>
            <Input
              monospace
              value={volumeField.text}
              onChange={volumeField.onChange}
              onBlur={volumeField.onBlur}
              placeholder="0"
              ariaLabel={t('requests:draft.containerTotals.volume')}
            />
          </Field>
        </Grid2>
      </div>
    </Card>
  );
}
